import React, { useEffect, useState } from 'react';
import { TUTORIAL_STEPS, TOTAL_TUTORIAL_STEPS } from '../../game/data/tutorialSteps';

// Spotlight tutorial overlay with a card tooltip
// Shows for steps 1+ (step 0 is the WelcomeModal)
export function TutorialOverlay({ tutorial, dispatch }) {
  const [rect, setRect] = useState(null);

  const stepIndex = tutorial?.currentStep;
  const step = (stepIndex !== null && stepIndex < TUTORIAL_STEPS.length) ? TUTORIAL_STEPS[stepIndex] : null;

  // Only show for steps with a target (step 0 = WelcomeModal)
  const shouldShow = tutorial?.enabled && !tutorial?.welcomeDismissed === false && step && step.step > 0 && step.action;

  // Find target element and track its position
  useEffect(() => {
    if (!shouldShow || !step?.target) {
      setRect(null);
      return;
    }
    function updateRect() {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    }
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [shouldShow, step?.target]);

  // Keyboard handlers
  useEffect(() => {
    if (!shouldShow) return;
    function handler(e) {
      if (e.key === 'Escape') dispatch({ type: 'TUTORIAL_SKIP' });
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shouldShow, dispatch]);

  if (!shouldShow || !step) return null;

  const stepNum = step.step;
  const totalSteps = TOTAL_TUTORIAL_STEPS - 1; // exclude welcome

  // Tooltip position: if rect found, show near the spotlight; otherwise center screen
  const tooltipStyle = rect
    ? {
        position: 'fixed',
        top: rect.top > 200 ? rect.top - 160 : rect.top + rect.height + 12,
        left: Math.max(16, Math.min(window.innerWidth - 320, rect.left + rect.width / 2 - 150)),
        width: 300,
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 320,
      };

  return (
    <>
      {/* Dark overlay with spotlight cutout */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 900,
        pointerEvents: 'none',
      }}>
        {rect ? (
          /* SVG-based spotlight — punches a hole around the target */
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
            <defs>
              <mask id="spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={rect.left - 6}
                  y={rect.top - 6}
                  width={rect.width + 12}
                  height={rect.height + 12}
                  rx="6"
                  fill="black"
                />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#spotlight-mask)" />
          </svg>
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
        )}
      </div>

      {/* Tooltip card */}
      <div style={{
        ...tooltipStyle,
        zIndex: 910,
        background: 'var(--panel)',
        border: '1px solid var(--green-dim)',
        borderRadius: 'var(--radius)',
        padding: '16px 18px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      }}>
        {/* Step counter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            Step {stepNum} of {totalSteps}
          </span>
          <div style={{ display: 'flex', gap: 3 }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: i + 1 === stepNum ? 'var(--green)' : i + 1 < stepNum ? 'var(--green-dim)' : 'var(--border)',
              }} />
            ))}
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>
          {step.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 }}>
          {step.body}
        </div>
        {step.action && (
          <div style={{ fontSize: 11, color: 'var(--green)', fontStyle: 'italic', marginBottom: 14 }}>
            → {step.action}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => dispatch({ type: 'TUTORIAL_SKIP' })}
            style={{ fontSize: 11 }}
          >
            Skip Tutorial
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => dispatch({ type: 'TUTORIAL_ADVANCE', payload: { step: stepIndex + 1 } })}
            style={{ fontSize: 11 }}
          >
            {step.completionCondition ? 'Next →' : 'Got it →'}
          </button>
        </div>
      </div>
    </>
  );
}
